<script setup>
import { computed, inject, onMounted, reactive, ref } from 'vue'
import { Pencil, RefreshCw } from '@lucide/vue'
import { Badge, Button, PageHeader, Section, Spinner, Table, useToast } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import ProductEditModal from '../../components/billing/ProductEditModal.vue'
import { useAdminAuth } from '../../composables/useAdminAuth.js'
import { formatInt } from '../llm/llm-format.js'
import { formatCurrency, turnsCoveredBy } from './format.js'

const api = inject('api')
if (!api) throw new Error('BillingProducts: api provider is required')
const { t } = useI18n()
const auth = useAdminAuth()
const toast = useToast()
const canEdit = computed(() => auth.account.value.role === 'admin')

const products = ref([])
// 折合回合 = 到账积分 ÷ 有效回合单价，单价来自收费规则，不在这页写死。
const turnPrice = ref(0)
const loading = ref(false)
const error = ref('')
const modal = reactive({ open: false, product: null, saving: false, error: '' })

const columns = computed(() => {
  const list = [
    { key: 'title', label: t('visualGameAdmin.billing.products.table.title'), width: '200px' },
    { key: 'id', label: t('visualGameAdmin.billing.products.table.id'), width: '200px', mono: true },
    { key: 'paid_points', label: t('visualGameAdmin.billing.products.table.paidPoints'), width: '120px', align: 'right', mono: true },
    { key: 'bonus_points', label: t('visualGameAdmin.billing.products.table.bonusPoints'), width: '120px', align: 'right', mono: true },
    { key: 'points', label: t('visualGameAdmin.billing.products.table.totalPoints'), width: '120px', align: 'right', mono: true },
    { key: 'turns', label: t('visualGameAdmin.billing.products.table.turns'), width: '120px', align: 'right', mono: true },
    { key: 'price_cents', label: t('visualGameAdmin.billing.products.table.price'), width: '110px', align: 'right', mono: true },
    { key: 'stripe_price_id', label: t('visualGameAdmin.billing.products.table.stripePrice'), width: '280px', mono: true },
    { key: 'active', label: t('visualGameAdmin.billing.products.table.active'), width: '100px' },
  ]
  if (canEdit.value) list.push({ key: 'actions', label: t('visualGameAdmin.common.actions'), width: '110px', align: 'right' })
  return list
})

onMounted(load)

async function load() {
  loading.value = true
  error.value = ''
  try {
    const [productPage, policyPage] = await Promise.all([
      api.commerce.listAdminProducts(),
      api.commerce.listAdminActionPolicies(),
    ])
    const turnPolicy = policyPage.items.find(policy => policy.action === 'turn_play')
    if (turnPolicy === undefined) throw new Error('Billing policy turn_play is missing')
    products.value = productPage.items
    turnPrice.value = turnPolicy.points
  } catch (cause) {
    error.value = cause.message
  } finally {
    loading.value = false
  }
}

function openEdit(product) {
  modal.product = product
  modal.error = ''
  modal.open = true
}

async function saveProduct(body) {
  modal.saving = true
  modal.error = ''
  try {
    const saved = await api.commerce.updateAdminProduct(modal.product.id, body)
    products.value = products.value.map(item => (item.id === saved.id ? saved : item))
    modal.open = false
    toast.success(t('visualGameAdmin.billing.saved'))
  } catch (cause) {
    modal.error = cause.message
  } finally {
    modal.saving = false
  }
}
</script>

<template>
  <div class="vga-page" data-insp-path="apps/visual-game-admin/src/pages/billing/ProductsPage.vue">
    <PageHeader :title="t('visualGameAdmin.billing.products.title')">
      <template #actions>
        <Badge v-if="!canEdit" variant="neutral">{{ t('visualGameAdmin.billing.readOnly') }}</Badge>
        <Button variant="secondary" size="sm" :loading="loading" @click="load">
          <RefreshCw :size="14" />{{ t('visualGameAdmin.common.refresh') }}
        </Button>
      </template>
    </PageHeader>
    <p class="vga-page-description">{{ t('visualGameAdmin.billing.products.description') }}</p>

    <Section :padded="false">
      <div v-if="loading && products.length === 0" class="vga-state"><Spinner /></div>
      <div v-else-if="error" class="vga-state">
        <p class="vga-error">{{ error }}</p>
        <Button variant="secondary" @click="load">{{ t('visualGameAdmin.common.retry') }}</Button>
      </div>
      <Table v-else :columns="columns" :rows="products">
        <template #paid_points="{ row }">{{ formatInt(row.paid_points) }}</template>
        <template #bonus_points="{ row }">{{ formatInt(row.bonus_points) }}</template>
        <template #points="{ row }">{{ formatInt(row.points) }}</template>
        <template #turns="{ row }">{{ t('visualGameAdmin.billing.turnsCovered', { n: turnsCoveredBy(row.points, turnPrice) }) }}</template>
        <template #price_cents="{ row }">{{ formatCurrency(row.price_cents, row.currency) }}</template>
        <template #stripe_price_id="{ row }">{{ row.stripe_price_id === null ? '—' : row.stripe_price_id }}</template>
        <template #active="{ row }">
          <Badge :variant="row.active ? 'success' : 'neutral'">
            {{ t(`visualGameAdmin.billing.products.activeState.${row.active ? 'on' : 'off'}`) }}
          </Badge>
        </template>
        <template #actions="{ row }">
          <Button variant="ghost" size="sm" @click="openEdit(row)">
            <Pencil :size="14" />{{ t('visualGameAdmin.common.edit') }}
          </Button>
        </template>
      </Table>
    </Section>

    <ProductEditModal
      v-model:open="modal.open"
      :product="modal.product"
      :turn-price="turnPrice"
      :saving="modal.saving"
      :error="modal.error"
      @submit="saveProduct"
    />
  </div>
</template>
